import { Box, IconButton } from "@mui/material";
import * as React from "react";

import { DeviceLink } from "@/shared/components/DeviceLink";
import { DeviceSelector } from "@/shared/components/devices";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { useUpdateDataset } from "@/shared/domain/datasets/hooks/useUpdateDataset";
import { OrgRecord } from "@/shared/domain/orgs";
import { CheckIcon, ClearIcon, EditIcon } from "@/shared/foundation/icons/mui";

import styles from "./DatasetDevice.module.css";

interface DatasetDeviceProps {
  datasetId: string;
  currentDeviceId: string | null;
  org: OrgRecord | null;
  onError: (error: Error) => void;
}

export function DatasetDevice({
  datasetId,
  currentDeviceId,
  org,
  onError,
}: DatasetDeviceProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [pendingDeviceId, setPendingDeviceId] = React.useState<string | null>(
    null,
  );

  const { mutate: updateDataset, isPending } = useUpdateDataset();

  const handleStartEdit = () => {
    setPendingDeviceId(currentDeviceId);
    setIsEditing(true);
  };

  const handleSave = () => {
    updateDataset(
      {
        datasetId: datasetId,
        resourceOwnerId: org?.org_id || "",
        updates: {
          device_id: pendingDeviceId,
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          setPendingDeviceId(null);
        },
        onError: (error: Error) => {
          if (onError) {
            onError(error);
          }
        },
      },
    );
  };

  const handleCancel = () => {
    setPendingDeviceId(null);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className={styles.editingContainer}>
        <DeviceSelector
          deviceId={pendingDeviceId}
          setDeviceId={setPendingDeviceId}
          orgId={org?.org_id}
          disabled={isPending}
          size="small"
        />
        <RobotoIconButton
          aria-label="save device"
          disabled={isPending}
          onClick={handleSave}
          size="small"
        >
          <CheckIcon
            fontSize="small"
            color={isPending ? "disabled" : "success"}
          />
        </RobotoIconButton>
        <RobotoIconButton
          aria-label="cancel edit"
          disabled={isPending}
          onClick={handleCancel}
          size="small"
        >
          <ClearIcon
            fontSize="small"
            color={isPending ? "disabled" : "error"}
          />
        </RobotoIconButton>
      </div>
    );
  }

  return (
    <Box className={styles.displayContainer}>
      {currentDeviceId ? <DeviceLink deviceId={currentDeviceId} /> : "N/A"}
      <IconButton
        size="small"
        onClick={handleStartEdit}
        aria-label="edit device"
        className={styles.editButton}
      >
        <EditIcon color="primary" className={styles.editIcon} />
      </IconButton>
    </Box>
  );
}
